import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { submitGateDecision } from '../api/client';

interface Props {
  projectId: string;
  isAboveTheLine: boolean;
  onSubmitSuccess: () => void;
  onCancel: () => void;
}

export default function ApprovalForm({ projectId, isAboveTheLine, onSubmitSuccess, onCancel }: Props) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    decision: 'Approved' as 'Approved' | 'Rejected' | 'Changes_Requested',
    rationale: '',
    priority: 'P3',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await submitGateDecision({
        projectId,
        gateNumber: 2,
        decision: form.decision === 'Approved' ? 'Approved' : 'Rejected',
        approvedByEmail: user!.email,
        approvedByName: user!.name,
        rationale: form.rationale,
        priority: form.priority as any,
      });

      if (response.success) {
        onSubmitSuccess();
      } else {
        setError(response.error || 'Failed to approve');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="approval-form">
      {error && <div className="error-banner">{error}</div>}

      <h3>Leadership Approval</h3>

      <div className="form-group">
        <label htmlFor="decision">Decision</label>
        <select name="decision" value={form.decision} onChange={handleChange}>
          <option value="Approved">Approve</option>
          <option value="Rejected">Reject</option>
          <option value="Changes_Requested">Request Changes</option>
        </select>
      </div>

      {form.decision === 'Approved' && isAboveTheLine && (
        <div className="form-group">
          <label htmlFor="priority">Priority</label>
          <select name="priority" value={form.priority} onChange={handleChange}>
            <option value="P1">P1 (Critical)</option>
            <option value="P2">P2 (High)</option>
            <option value="P3">P3 (Medium)</option>
            <option value="P4">P4 (Low)</option>
          </select>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="rationale">Rationale</label>
        <textarea name="rationale" value={form.rationale} onChange={handleChange} required rows={4} />
      </div>

      <div className="form-actions">
        <button type="submit" disabled={loading}>
          {loading ? 'Submitting...' : 'Submit Decision'}
        </button>
        <button type="button" onClick={onCancel}>Cancel</button>
      </div>

      <style jsx>{`
        .approval-form {
          background: white;
          padding: 15px;
          border-radius: 8px;
          border: 1px solid #ddd;
        }

        .approval-form h3 {
          margin-top: 0;
          margin-bottom: 15px;
          font-size: 16px;
        }

        .form-group {
          margin-bottom: 15px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          font-size: 14px;
        }

        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-family: inherit;
          font-size: 14px;
        }

        .form-group textarea {
          resize: vertical;
        }

        .form-actions {
          display: flex;
          gap: 10px;
          margin-top: 15px;
        }

        .form-actions button {
          flex: 1;
        }

        button {
          padding: 10px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        button:hover:not(:disabled) {
          background: #0056b3;
        }

        button:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .error-banner {
          padding: 10px;
          background: #ffebee;
          color: #d32f2f;
          border-radius: 4px;
          margin-bottom: 12px;
          font-size: 13px;
        }
      `}</style>
    </form>
  );
}
